import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { captableAction } from 'common/actions';
import { CASH_FLOW_LEDGER_LOCKED_MESSAGE, CASH_FLOW_LEDGER_SLUG } from 'common/constants/cap-table';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Alert, AllocationFinalMessage, CustomContainer } from 'components';
import { FinalAllocation } from 'components/AllocationFinalMessage/AllocationFinalMessage.props';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useLayoutContext } from 'context/LayoutContext';
import { CashFlowsProps } from 'pages/CapTable/cash-distributions/CashFlows.props';
import { useAllSecurityOptions } from 'pages/CapTable/cash-distributions/hooks/useAllSecurityOptions';
import { useEntityOptions } from 'pages/CapTable/cash-distributions/hooks/useEntityOptions';
import { useOwnershipOptions } from 'pages/CapTable/cash-distributions/hooks/useOwnershipOptions';
import { useTableValidation } from 'services/hooks';
import { useCaptableCashFlowLedger } from 'services/hooks/captable';
import { Logger } from 'utilities';
import { GrossIRRSummary } from './components/GrossIRRSummary';
import CashFlowContext from './context/CashFlowContext';
import { CashFlowLedgerItem } from './data/types';
import { useCashFlowLedgerExport } from './hooks/useCashFlowLedgerExport';
import { useCashDistributionsSpreadsheetConfig } from './hooks/useCashFlowLedgerSpreadsheetConfig';
import { useGrossIRRSummary } from './hooks/useGrossIRRSummary';

const CashFlows = ({ capTableVersionsByCompany, selectedMeasurementDate, isActiveTab = true, saveAsNewProforma }: CashFlowsProps) => {
  const { fetchCashFlowLedger, updateCashFlowLedger } = useCaptableCashFlowLedger();
  const { exportCashFlowLedger, isExporting, canExport } = useCashFlowLedgerExport();
  const {
    updatePageActions,
    updateExtraPageActions,
    togglePdfDialog,
    toggleExportDialog,
    toggleMainForm,
    viewOnlyUser,
    isExportMode,
  } = useLayoutContext();
  const [
    {
      captableInfo,
      fundList,
      companyInfo,
      firmInfo,
    },
  ] = useStore() as unknown as UseStoreValues;
  const [cashFlows, setCashFlows] = useState<CashFlowLedgerItem[]>([]);
  const [isAllocationFinal, setIsAllocationFinal] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [finalAllocations, setFinalAllocations] = useState<FinalAllocation[]>([]);
  // Keep a ref to cashFlows for use in callbacks
  const cashFlowsRef = useRef<CashFlowLedgerItem[]>([]);
  useEffect(() => {
    cashFlowsRef.current = cashFlows;
  }, [cashFlows]);

  const sortCashFlowsByDate = useCallback((flows: CashFlowLedgerItem[]) => [...flows].sort((a, b) => {
    const dateA = a.date_of_transaction ? new Date(a.date_of_transaction).getTime() : 0;
    const dateB = b.date_of_transaction ? new Date(b.date_of_transaction).getTime() : 0;
    return dateA - dateB;
  }), []);

  // Merge current spreadsheet data with persisted data
  const mergeSpreadsheetDataWithPersisted = useCallback((spreadsheetData: CashFlowLedgerItem[]) => {
    const persistedFlows = cashFlowsRef.current;

    // Create a map of reversed data by ID (for items that have IDs)
    const reversedById = new Map<number, CashFlowLedgerItem>();
    const newItems: CashFlowLedgerItem[] = [];

    spreadsheetData.forEach(item => {
      if (item.id) {
        reversedById.set(item.id, item);
      } else {
        newItems.push(item);
      }
    });

    // Only keep persisted items that are saved (id) or virtual.
    const persistedItems = persistedFlows.filter(item => item.id || item.is_virtual);

    // Start with persisted items, updating with any changes from the spreadsheet
    const mergedItems = persistedItems.map(persistedItem => {
      if (persistedItem.id && reversedById.has(persistedItem.id)) {
        // Use the updated version from the spreadsheet
        return reversedById.get(persistedItem.id)!;
      }
      // Keep the original persisted item
      return persistedItem;
    });

    // Add any new items (without IDs) from the spreadsheet
    return [...mergedItems, ...newItems];
  }, []);

  const { securityOptionsByEntity, loading: securityOptionsLoading } = useAllSecurityOptions({ companyInfo, captableInfo, capTableVersionsByCompany });

  // Placeholder ref for spreadsheet - will be populated after config is created
  const spreadsheetRef = useRef<typeof spreadsheet>(null);

  const handleDeleteRow = useCallback((rowIndex: number) => {
    const currentSpreadsheet = spreadsheetRef.current;
    if (!currentSpreadsheet) {
      return;
    }

    // Get current spreadsheet data including unsaved changes
    const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();

    // Merge with persisted data to avoid losing database rows
    const mergedData = mergeSpreadsheetDataWithPersisted(reversedData);

    // Remove the row at the specified index (rowIndex is 1-based, so subtract 1)
    const updatedFlows = [...mergedData];
    updatedFlows.splice(rowIndex - 1, 1);
    setCashFlows(updatedFlows);
  }, [mergeSpreadsheetDataWithPersisted]);

  const { spreadsheet } = useCashDistributionsSpreadsheetConfig({
    ledgerData: cashFlows,
    currency: companyInfo?.captable_currency || 'USD',
    isDisabled,
    securityOptionsByEntity,
    onDeleteRow: handleDeleteRow,
  });

  // Update spreadsheet ref when spreadsheet changes
  useEffect(() => {
    spreadsheetRef.current = spreadsheet;
  }, [spreadsheet]);

  const ownershipOptions = useOwnershipOptions({ companyInfo, fundList, cashFlows });
  const entityOptions = useEntityOptions({ companyInfo });

  const cashFlowContextValue = useMemo(() => ({
    ownershipOptions,
    entityOptions,
    securityOptionsByEntity,
    securityOptionsLoading,
  }), [ownershipOptions, entityOptions, securityOptionsByEntity, securityOptionsLoading]);

  const spreadsheets = useMemo(() => (spreadsheet ? [spreadsheet] : []), [spreadsheet]);
  const {
    onChange,
    cells,
    workbook,
    workbookContextValue,
    data,
    setCells,
  } = useWorkbook(spreadsheets);
  const { validateCells } = useTableValidation();

  // Note: We intentionally do NOT sync spreadsheet data to cashFlows on every edit
  // because that causes the spreadsheet config to be recreated, causing re-renders.
  // The IRR calculation will use slightly stale data until the next save/refresh,
  // but this is acceptable for better UX.

  // Fetch gross IRR data for the summary table (must be after ownershipOptions is created)
  const { fundsGrossIrr, firmGrossIrr, fundIdToName } = useGrossIRRSummary({
    fundList,
    cashFlows,
    ownershipOptions,
  });

  // Refetch cash flow ledger when the tab becomes active to pick up changes from Fund Ownership
  useEffect(() => {
    if (isActiveTab && captableInfo?.id) {
      fetchCashFlowLedger(captableInfo.id).then(flows => {
        setCashFlows(sortCashFlowsByDate(flows || []));
      });
    }
  }, [captableInfo?.id, fetchCashFlowLedger, sortCashFlowsByDate, isActiveTab]);

  // Check allocation finalization status on mount
  useEffect(() => {
    if (captableInfo?.id) {
      const verifyAllocationStatus = async () => {
        try {
          const allocations = (await captableAction.verifyIfHasFinalAllocation(captableInfo.id!)) as FinalAllocation[];

          setIsAllocationFinal(!isEmpty(allocations));
          setIsDisabled(!isEmpty(allocations) || viewOnlyUser);
          setFinalAllocations(allocations);

          if (isExportMode) {
            setIsAllocationFinal(true);
            setIsDisabled(true);
          }
        } catch (error) {
          Logger.error('Error verifying allocation status:', error);
        }
      };
      verifyAllocationStatus();
    }
  }, [captableInfo?.id, viewOnlyUser, isExportMode]);

  // Callback for duplicate/save as proforma
  const savesAsProformaConfirmation = useCallback(() => {
    saveAsNewProforma?.({});
  }, [saveAsNewProforma]);

  const handleAddRow = useCallback(() => {
    const currentSpreadsheet = spreadsheetRef.current;
    if (!currentSpreadsheet) {
      return;
    }

    // Get current spreadsheet data including unsaved changes
    const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();

    // Merge with persisted data to avoid losing database rows
    const mergedData = mergeSpreadsheetDataWithPersisted(reversedData);

    const newItem: CashFlowLedgerItem = {
      date_of_transaction: '',
      amount: 0,
      transaction_type: '',
      is_virtual: false,
    };

    // Add new row to the merged data
    setCashFlows([...mergedData, newItem]);
  }, [mergeSpreadsheetDataWithPersisted]);

  const handleAddMultipleRows = useCallback((numRows = 1) => {
    const currentSpreadsheet = spreadsheetRef.current;
    if (!currentSpreadsheet) {
      return;
    }

    // Get current spreadsheet data including unsaved changes
    const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();

    // Merge with persisted data to avoid losing database rows
    const mergedData = mergeSpreadsheetDataWithPersisted(reversedData);

    // Create multiple new items
    const newItems: CashFlowLedgerItem[] = Array.from({ length: numRows }, () => ({
      date_of_transaction: '',
      amount: 0,
      transaction_type: '',
      is_virtual: false,
    }));

    // Add all new rows to the merged data
    setCashFlows([...mergedData, ...newItems]);
  }, [mergeSpreadsheetDataWithPersisted]);

  const handleSave = useCallback(async () => {
    const currentSpreadsheet = spreadsheetRef.current;
    if (!currentSpreadsheet || !captableInfo?.id) {
      return;
    }

    const sheetName = currentSpreadsheet.name;
    const sheetCells = sheetName ? cells?.[sheetName] : undefined;
    if (!sheetCells) {
      return;
    }

    const cellsToValidate = Object.values(sheetCells).filter(
      cell => !cell.readOnly || (cell as any).validateWhenReadOnly
    );
    const { areCellsValid: areSheetCellsValid } = validateCells({
      cellsToValidate,
      customValidations: currentSpreadsheet.customValidations,
      parsedColumns: currentSpreadsheet.cells,
      tableData: currentSpreadsheet.tableData,
      isSaving: true,
      useOriginalCell: true,
    });

    if (sheetName) {
      setCells(prev => ({
        ...prev,
        [sheetName]: { ...sheetCells },
      }));
    }

    if (!areSheetCellsValid) {
      return;
    }

    const reversedData: CashFlowLedgerItem[] = currentSpreadsheet.reverseParse();
    try {
      await updateCashFlowLedger(captableInfo.id, reversedData);
      // Refresh cash flows after save to get updated IDs and resort
      const flows = await fetchCashFlowLedger(captableInfo.id);
      setCashFlows(sortCashFlowsByDate(flows || []));
    } catch {
      // Error is logged in the hook
    }
  }, [
    captableInfo?.id,
    cells,
    fetchCashFlowLedger,
    setCells,
    sortCashFlowsByDate,
    updateCashFlowLedger,
    validateCells,
  ]);

  // Keep a ref to handleSave to provide a stable callback to updatePageActions
  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  // Keep a ref to exportCashFlowLedger for stable callback
  const exportRef = useRef(exportCashFlowLedger);
  useEffect(() => {
    exportRef.current = exportCashFlowLedger;
  }, [exportCashFlowLedger]);

  useEffect(() => {
    if (isActiveTab && !isEmpty(captableInfo)) {
      updatePageActions?.({
        mainAction: {
          id: CASH_FLOW_LEDGER_SLUG,
          label: 'Save',
          isActive: isDisabled,
          isLock: isAllocationFinal,
          callback: () => handleSaveRef.current(),
        },
      });
    }
    return () => {
      if (isActiveTab) {
        updatePageActions?.(null);
      }
    };
  }, [
    toggleExportDialog,
    toggleMainForm,
    togglePdfDialog,
    updatePageActions,
    updateExtraPageActions,
    captableInfo,
    canExport,
    companyInfo,
    isExporting,
    isActiveTab,
    isDisabled,
    isAllocationFinal,
  ]);

  if (!spreadsheet) {
    return (
      <CustomContainer>
        <GridSkeleton />
      </CustomContainer>
    );
  }

  return (
    <CustomContainer>
      <Grid container spacing={2}>
        {/* Locked state banner with Unlock/Duplicate */}
        {isDisabled && isAllocationFinal && !isExportMode && (
          <Grid size={{ xs: 12 }}>
            <AllocationFinalMessage
              tableTerms={{ tableName: 'Cash Flow Ledger' }}
              finalAllocations={finalAllocations}
              setIsDisabled={setIsDisabled}
              savesAsProformaConfirmation={savesAsProformaConfirmation}
              variant
              actions={[
                {
                  label: 'Duplicate',
                  buttonProps: {
                    variant: 'contained',
                    size: 'small',
                    disabled: viewOnlyUser,
                  },
                  callback: savesAsProformaConfirmation,
                },
              ]}
              viewOnlyUser={viewOnlyUser}
            />
          </Grid>
        )}

        {/* Alert when unlocked but still final */}
        {isAllocationFinal && !isDisabled && !isExportMode && (
          <Grid size={{ xs: 12 }}>
            <Alert isAlertVisible>{CASH_FLOW_LEDGER_LOCKED_MESSAGE}</Alert>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <CashFlowContext.Provider value={cashFlowContextValue}>
            <WorkbookContext.Provider value={workbookContextValue}>
              {cells && data && <ScalarSpreadsheet
                {...spreadsheet}
                cells={cells}
                showTitlesColumn={false}
                showToolbar={false}
                onChange={onChange}
                workbook={workbook}
                sheet={spreadsheet}
                data={data}
                displayDeleteRowBtn={!isDisabled}
                deleteRowFn={handleDeleteRow}
                addMultipleRows={handleAddMultipleRows}
              />}
            </WorkbookContext.Provider>
          </CashFlowContext.Provider>
        </Grid>
        {!isExportMode && (
          <Grid size={{ xs: 12 }} sx={{ pl: '12px' }}>
            <Button onClick={handleAddRow} variant="contained" disabled={isDisabled}>
              Add Transaction
            </Button>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <GrossIRRSummary
            fundsGrossIrr={fundsGrossIrr}
            firmGrossIrr={firmGrossIrr}
            fundIdToName={fundIdToName}
            firmName={companyInfo?.name || firmInfo?.name || 'Total'}
          />
        </Grid>
      </Grid>
    </CustomContainer>
  );
};

export default CashFlows;
